import { useState, useEffect, useRef } from 'react';
import {
  Input,
  Card,
  CardBody,
  ListGroup,
  ListGroupItem,
} from 'reactstrap';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { getPlacesEndpoint } from '../../../services/addressService';

interface PlaceSearchBarProps {
  isLoading: boolean;
  onPlaceSelect: (place: any) => void;
  callEndpoint: any;
  token: string;
}

const PlaceSearchBar = ({
  isLoading,
  onPlaceSelect,
  callEndpoint,
  token,
}: PlaceSearchBarProps) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showResults, setShowResults] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const isSelecting = useRef<boolean>(false);

  useEffect(() => {
    if (isSelecting.current) {
      isSelecting.current = false;
      return;
    }

    const searchPlaces = async () => {
      if (!searchQuery.trim()) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      try {
        const { status, data } = await callEndpoint(
          getPlacesEndpoint({
            input: searchQuery,
            not_found: '1',
            sid: '',
          }),
        );

        if (status === 200) {
          const response = fetchResponseAdapter(data);
          setSearchResults(response?.data || []);
          setShowResults(true);
        } else {
          setSearchResults([]);
          setShowResults(false);
        }
      } catch (error) {
        console.error('Error searching places:', error);
        setSearchResults([]);
        setShowResults(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      searchPlaces();
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, token, callEndpoint]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowResults(value.trim().length > 0);
  };

  const handlePlaceSelect = (place: any) => {
    isSelecting.current = true;
    setSearchQuery(place.value);
    setShowResults(false);
    setSearchResults([]);
    onPlaceSelect(place);
  };

  const handleSearchBlur = () => {
    // Delay hiding results to allow clicking on them
    setTimeout(() => setShowResults(false), 200);
  };

  return (
    <div style={{ position: 'relative' }}>
      <Input
        type="text"
        placeholder="Buscar lugares cercanos..."
        value={searchQuery}
        onChange={handleSearchChange}
        onBlur={handleSearchBlur}
        onFocus={() => searchQuery.trim() && setShowResults(true)}
      />
      {!isLoading && showResults && searchResults.length > 0 && (
        <Card
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: '300px',
            overflowY: 'auto',
            marginTop: '2px',
          }}
        >
          <CardBody className="p-0">
            <ListGroup flush>
              {searchResults.map((place) => (
                <ListGroupItem
                  key={place.id}
                  action
                  onClick={() => handlePlaceSelect(place)}
                  style={{ cursor: 'pointer' }}
                >
                  <div>
                    <strong>{place.value}</strong>
                    {place.types && place.types.length > 0 && (
                      <span className="text-muted ms-2">
                        ({place.types[0]})
                      </span>
                    )}
                  </div>
                  {place.location?.coordinates && (
                    <small className="text-muted">
                      Lat: {place.location.coordinates[1]}, Lng:{' '}
                      {place.location.coordinates[0]}
                    </small>
                  )}
                </ListGroupItem>
              ))}
            </ListGroup>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default PlaceSearchBar;
